import { Navigate } from 'react-router-dom';
import { ReactNode } from 'react';
import { useAuth } from 'src/containers/contexts/auth-provider';
import { Box } from '@mui/material';
import Loader from '../atoms/loader/loader';
import { authRoutes } from 'src/features/tenant/constants/auth-routes';
import { useAvailableSites } from 'src/hooks/api/use-available-sites';
import { SiteType } from 'src/types/enum/site-type';

interface Props {
  children: ReactNode;
}

export const AuthGuard = ({ children }: Props) => {
  const auth = useAuth();
  const isLoggedIn = !!auth.session?.access_token;

  const { data: availableSitesData, isLoading: isLoadingAvailableSites } =
    useAvailableSites(isLoggedIn);

  const hasAvailableSites =
    availableSitesData &&
    availableSitesData?.resources?.filter(
      site => site.type === SiteType.RESIDENTIAL,
    )?.length >= 1;

  if (auth.loading || isLoadingAvailableSites) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
        }}>
        <Loader />
      </Box>
    );
  }

  if (!auth.session) {
    const returnUrl = encodeURIComponent(window.location.pathname);
    // When reset password links is expired and user has no session we redirect to forgot-password page

    const isError = window.location.href.includes('error_code=401');

    if (!authRoutes.includes(window.location.pathname) && isError) {
      //we also check if there is any error in the url

      return (
        <Navigate
          to={`/forgot-password?returnUrl=${returnUrl}&expired=${isError}`}
        />
      );
    }

    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  if (!hasAvailableSites) {
    return <Navigate to={'/no-sites-available'} replace />;
  }

  return children;
};
